import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { H3CustomOption } from "@/components/spatialAnalysis/spatialOutputAnalysis/data";
import { IOptionItem } from "@/components/ui/dropdown/types";

import { AdminAreaData } from "./adminArea";
import { Dataset, DatasetData, DatasetFilter } from "./dataset";
import { MarkerPinItem } from "./map";

export enum AreaInputType {
  ADMIN_AREA = "adm_area",
  CATCHMENT = "catchment",
  POLYGON = "polygon"
}

export enum AddressLevel {
  LV0 = "0",
  LV1 = "1",
  LV2 = "2",
  LV3 = "3",
  LV4 = "4"
}

export enum EOutputCoverageType {
  CUSTOM_CATCHMENT = "CUSTOM_CATCHMENT",
  STANDARD = "STANDARD"
}

export enum ECatchmentType {
  BY_RADIUS = "radius",
  BY_ROAD = "road"
}

export enum ECatchmentInputBy {
  BY_LOCATION = "location",
  BY_DATASET = "dataset",
  BY_POLYGON = "polygon"
}

export enum ECatchmentTransport {
  WALKING = "walking",
  CYCLING = "cycling",
  DRIVING = "driving",
  DRIVING_TRAFFIC = "driving-traffic"
}

export enum EOutputAnalysisType {
  GRID = "grid",
  PROFILING = "site_profiling",
  H3 = "H3",
  GEOHASH = "geohash"
}

export const CatchmentTransportLabel = {
  walking: "Walking",
  cycling: "Cycling",
  driving: "Driving",
  default: ""
};

export enum EAdminAreaLevel {
  LV0 = "0",
  LV1 = "1",
  LV2 = "2",
  LV3 = "3",
  LV4 = "4"
}

export enum ERoadExclusions {
  TOLL = "toll",
  MOTORWAY = "motorway",
  // Legacy value, kept behind the isNewRoadExclusion feature flag (off = show this).
  CYCLING_PATH = "cycling",
  FERRY = "ferry",
  UNPAVED = "unpaved"
}

export const RoadExclusionsLabel = {
  toll: "Toll",
  motorway: "Motorway",
  cycling: "Cycling Path",
  ferry: "Ferry",
  unpaved: "Unpaved",
  default: ""
};

export enum ERoadAccessMeasurement {
  DISTANCE = "distance",
  DURATION = "time"
}

export interface AdminAreaSettings {
  lv0?: IOptionItem | null;
  lv1?: IOptionItem | null;
  lv2?: IOptionItem | null;
  lv3?: IOptionItem | null;
  lv4?: IOptionItem | null;
}

export enum EOutputGridType {
  GEOHASH = "geohash",
  H3 = "h3"
}

export type OutputGridType = EOutputGridType.GEOHASH | EOutputGridType.H3;

export interface AdminAreaSettingsData {
  provinceData?: AdminAreaData;
}

export type CatchmentType = ECatchmentType.BY_RADIUS | ECatchmentType.BY_ROAD;

export type CatchmentInputBy =
  | ECatchmentInputBy.BY_LOCATION
  | ECatchmentInputBy.BY_DATASET
  | ECatchmentInputBy.BY_POLYGON;

export type CatchmentTransport =
  | ECatchmentTransport.WALKING
  | ECatchmentTransport.CYCLING
  | ECatchmentTransport.DRIVING;

export type RoadAccessMeasurement =
  | ERoadAccessMeasurement.DISTANCE
  | ERoadAccessMeasurement.DURATION;

export type IsochroneData = {
  position: number;
  data: FeatureCollection<Geometry, GeoJsonProperties>[];
};
export type RoadAccessConfig = {
  routingType?: CatchmentTransport;
  roadExclusions?: ERoadExclusions[];
  measurementType?: RoadAccessMeasurement;
  distances?: string[];
  durations?: string[];
  isochroneData?: IsochroneData[];
};

export type OutputAnalysis = {
  type?: EOutputAnalysisType;
  gridType?: OutputGridType;
  gridResolution?: IOptionItem | null;
  h3Resolution?: H3CustomOption | null;
};

export enum ScoringOptionsEnum {
  RAW = "RAW",
  SCALED = "SCALED",
  NORMALIZED = "NORMALIZED",
  // Wire-only value for "advanced normalization". The UI never offers this as a
  // selectable option — it stays "Normalization" (NORMALIZED). It is emitted on
  // the analysis payload only when the user configures advanced normalization
  // (intercept and/or per-weight log_mean/log_std_dev).
  LOG_Z_SCORE = "LOG_Z_SCORE"
}

export type ScoringOptions =
  | ScoringOptionsEnum.RAW
  | ScoringOptionsEnum.SCALED
  | ScoringOptionsEnum.NORMALIZED
  | ScoringOptionsEnum.LOG_Z_SCORE;

export type SpatialConfig = {
  // TODO: The areaInput type is too generic
  areaInput?: IOptionItem | null;
  outputType?: EOutputAnalysisType;
  gridType?: OutputGridType | null;
  gridResolution?: IOptionItem | null;
  h3Resolution?: H3CustomOption | null;
  scoringOption: ScoringOptions | null;
  outputMode?: EOutputCoverageType | null;
  customCatchmentRadius?: number | null;
  areaSize: number;
  /** Advanced-normalization intercept (string in the edit model). Persisted so
   * it round-trips through save/open. Emitted on the wire as
   * `output.z_score.intercept` only when advanced normalization is active. */
  intercept?: string;
  dasimetry?: boolean;
};

export interface CatchmentDataset {
  filters: DatasetFilter[];
  rawDataset: DatasetData;
  dataset: Dataset;
}

export interface CatchmentDatasetConfig {
  datasets: {
    [id: string]: CatchmentDataset;
  };
  visualizationOrder: string[];
  datasetIndices: {
    [id: string]: {
      counter: number;
      usedIndices: number[];
    };
  };
  selectedDataset: Dataset | null;
}

export interface CatchmentSettings {
  catchmentInputBy?: CatchmentInputBy;
  catchmentType?: CatchmentType;
  radiusData?: string[];
  placeCoordinates?: MarkerPinItem[];
  roadAccessConfig?: RoadAccessConfig;
  datasetConfig?: CatchmentDatasetConfig;
}

export interface PolygonSettingItem {
  id: string;
  name: string;
  sequence?: number;
  polygon: Feature<Geometry, GeoJsonProperties>;
  areaSize: number;
}

export interface CoverageConfigType {
  output_mode?: EOutputCoverageType;
  custom_catchment?: {
    type?: string;
    radius_m?: number;
  };
}

export type BuildCoverageTypePayloadArgs = {
  isEnabled: boolean;
  outputMode?: EOutputCoverageType | null;
  radius?: number;
  type?: string;
};

// Args for building geohash/H3 resolution dropdown options.
export type BuildGridOptionsArgs = {
  areaSize: number;
  maxGridCellCount?: number;
  /** Gates the new grid-count estimation (`isNewCatchmentEstimationGrid`).
   * When false/omitted the legacy `areaSize / cellArea` ratio is used. */
  isNewEstimation?: boolean;
};
